import { useEffect, useMemo, useState } from 'react';
import { Modal, Button, Form, Alert, Row, Col, Badge, Spinner } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faTriangleExclamation,
  faLock,
  faUpRightFromSquare
} from '@fortawesome/free-solid-svg-icons';
import { drumLengthStandardUrl } from '../../helpers/drumLengthLink';

import axios from 'axios';

import { http } from '../../features/jobs/api/http';
import { API } from '../../config/api';
import { useDrums } from '../../features/drums/hooks/useDrums.js';
import {
  getDrumCapacityMeters,
  getCableOuterDiameter
} from '../../helpers/drumCapacity.js';

// Correcting a delivery-plan item after the order exists.
//
// A plan is typed by hand and sometimes typed wrong — 100 coils of 150 m for
// what should have been 150 of 100 — and until now the only way out was to
// cancel the order, throwing away every launch made against it.
//
// Deliberately NOT the creation planner (`DeliveryPlanModal`): that one works
// on a plan shape of drums[]/coils[] and has no notion of a saved `itemId`.
// Mapping saved items into it and back is exactly where this would go wrong,
// and the identity the endpoint needs would be the thing lost.
//
// Preview first, like every other repair here. The consequence worth seeing is
// not the item — it is the promises: a job that reserved 20 units of 150 m is
// holding 3 000 m, and after a re-spec it must hold 30 units of 100 m or it
// has quietly been cut.

const num = v => {
  const n = Number(String(v ?? '').replace(',', '.'));
  return Number.isFinite(n) ? n : 0;
};
const fmt = n => Math.round(Number(n) || 0).toLocaleString();

const EditPlanItemModal = ({ show, onHide, orderId, lineId, item, product, onSaved }) => {
  const [qtyPlanned, setQtyPlanned] = useState('');
  const [lengthPerUnit, setLengthPerUnit] = useState('');
  const [kind, setKind] = useState('');
  const [reason, setReason] = useState('');

  const [drumMaterial, setDrumMaterial] = useState('');
  const [drumDiameter, setDrumDiameter] = useState('');

  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [blockers, setBlockers] = useState([]);

  useEffect(() => {
    if (!show || !item) return;
    setQtyPlanned(String(item.qtyPlanned ?? ''));
    setLengthPerUnit(String(item.lengthPerUnit ?? ''));
    setKind(String(item.kind ?? ''));
    setDrumMaterial(String(item.drumMaterial ?? ''));
    setDrumDiameter(String(item.drumDiameter ?? ''));
    setReason('');
    setPreview(null);
    setErr('');
    setBlockers([]);
  }, [show, item]);

  // Anything already made freezes the spec: those coils exist in the yard and
  // do not become a different length because a field was edited. Said here as
  // well as refused by the server — a disabled field with a reason beats a
  // 409 after typing.
  const made = Math.max(Number(item?.qtyProduced) || 0, (item?.tokenIds || []).length ? 1 : 0);
  const specFrozen = made > 0;

  // The drum library comes from the database, same as the create-new planner —
  // a hardcoded list here would be the fourth copy and the first to go stale.
  const { data: drumsData } = useDrums();
  const drumLibrary = useMemo(() => drumsData?.data || [], [drumsData]);

  const drumMaterials = useMemo(
    () => [...new Set(drumLibrary.map(d => String(d.drumMaterial || '').trim()).filter(Boolean))],
    [drumLibrary]
  );
  const diametersFor = material =>
    [
      ...new Set(
        drumLibrary
          .filter(d => String(d.drumMaterial || '').toUpperCase() === String(material || '').toUpperCase())
          .map(d => Number(d.drumDiameter))
          .filter(Boolean)
      ),
    ].sort((a, b) => a - b);

  // How many metres fit is a function of the CABLE, not of the drum alone, so
  // the standard has to be read for its finished outer diameter. Without it
  // the figure would be a guess dressed up as a number, and none is shown.
  const [cableOuterDiameter, setCableOuterDiameter] = useState(0);
  useEffect(() => {
    if (!show || !product?.cableName) {
      setCableOuterDiameter(0);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.post(`${API.BACKEND}/api/v1/standard/find-many`, [
          {
            name: product.cableName,
            numberOfConductors: product.numberOfConductors,
            section: product.section,
            color: product.color,
          },
        ]);
        const std = (res?.data?.data || res?.data || [])[0];
        if (!cancelled) setCableOuterDiameter(getCableOuterDiameter(std?.phases || []) || 0);
      } catch {
        if (!cancelled) setCableOuterDiameter(0);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [show, product]);

  const isDrum = String(kind).toUpperCase() === 'DRUM';
  const maxMeters = useMemo(() => {
    if (!isDrum) return null;
    return getDrumCapacityMeters({
      drumMaterial,
      drumDiameter: num(drumDiameter),
      cableDiameter: cableOuterDiameter,
      drumLibrary,
    });
  }, [isDrum, drumMaterial, drumDiameter, cableOuterDiameter, drumLibrary]);

  const overCapacity = maxMeters != null && num(lengthPerUnit) > maxMeters;

  const body = useMemo(() => {
    const out = { reason };
    if (num(qtyPlanned) !== Number(item?.qtyPlanned)) out.qtyPlanned = num(qtyPlanned);
    if (!specFrozen) {
      if (num(lengthPerUnit) !== Number(item?.lengthPerUnit)) out.lengthPerUnit = num(lengthPerUnit);
      if (kind && kind !== item?.kind) out.kind = kind;
      if (drumMaterial !== String(item?.drumMaterial ?? '')) out.drumMaterial = drumMaterial || null;
      if (num(drumDiameter) !== Number(item?.drumDiameter || 0)) {
        out.drumDiameter = num(drumDiameter) || null;
      }
    }
    return out;
  }, [qtyPlanned, lengthPerUnit, kind, drumMaterial, drumDiameter, reason, item, specFrozen]);

  const changedFields = Object.keys(body).filter(k => k !== 'reason');
  const totalAfter = num(qtyPlanned) * num(lengthPerUnit);
  const totalBefore = Number(item?.qtyPlanned || 0) * Number(item?.lengthPerUnit || 0);

  const call = async dryRun => {
    setErr('');
    setBlockers([]);
    setBusy(true);
    try {
      const { data } = await http.post(
        `/api/redis/orders/${encodeURIComponent(orderId)}/lines/${encodeURIComponent(
          lineId
        )}/items/${encodeURIComponent(item.itemId)}`,
        { ...body, dryRun }
      );
      if (dryRun) {
        setPreview(data);
      } else {
        setPreview(null);
        onSaved?.(data);
        onHide?.();
      }
    } catch (e) {
      const d = e?.response?.data;
      // A refusal is not a failure: the 409 carries the reasons, and they are
      // the most useful thing that can be on this screen.
      if (d?.blockers?.length) setBlockers(d.blockers);
      else setErr(d?.error || e?.message || 'Failed.');
      setPreview(null);
    } finally {
      setBusy(false);
    }
  };

  if (!item) return null;

  return (
    <Modal show={show} onHide={onHide} centered size="lg">
      <Modal.Header closeButton>
        <div>
          <Modal.Title className="fs-7">Correct this delivery item</Modal.Title>
          <div className="fs-10 text-700">
            {item.kind} · currently {fmt(item.qtyPlanned)} × {fmt(item.lengthPerUnit)} m ={' '}
            {fmt(totalBefore)} m
          </div>
        </div>
      </Modal.Header>

      <Modal.Body>
        {specFrozen && (
          <Alert variant="warning" className="py-2 fs-9">
            <FontAwesomeIcon icon={faLock} className="me-1" />
            {made} unit(s) have already been made, so the length per unit and the kind are
            fixed — those coils exist. Only the quantity can change, and not below what
            was produced.
          </Alert>
        )}

        <Row className="g-3">
          <Col xs={6} md={4}>
            <Form.Label className="fs-10 mb-1">Quantity</Form.Label>
            <Form.Control
              size="sm"
              className="no-spin"
              inputMode="decimal"
              value={qtyPlanned}
              onChange={e => {
                setPreview(null);
                setQtyPlanned(e.target.value);
              }}
            />
          </Col>
          <Col xs={6} md={4}>
            <Form.Label className="fs-10 mb-1">Metres per unit</Form.Label>
            <Form.Control
              size="sm"
              className="no-spin"
              inputMode="decimal"
              disabled={specFrozen}
              value={lengthPerUnit}
              onChange={e => {
                setPreview(null);
                setLengthPerUnit(e.target.value);
              }}
            />
          </Col>
          <Col xs={12} md={4}>
            <Form.Label className="fs-10 mb-1">Kind</Form.Label>
            <Form.Select
              size="sm"
              disabled={specFrozen}
              value={kind}
              onChange={e => {
                setPreview(null);
                setKind(e.target.value);
              }}
            >
              <option value="COIL">COIL</option>
              <option value="DRUM">DRUM</option>
            </Form.Select>
          </Col>
        </Row>

        {isDrum && (
          <Row className="g-3 mt-1">
            <Col xs={6} md={4}>
              <Form.Label className="fs-10 mb-1">Drum type</Form.Label>
              <Form.Select
                size="sm"
                disabled={specFrozen}
                value={drumMaterial}
                onChange={e => {
                  setPreview(null);
                  setDrumMaterial(e.target.value);
                  // The diameters are per type, so a type change invalidates
                  // whatever was selected.
                  setDrumDiameter('');
                }}
              >
                <option value="">— choose —</option>
                {drumMaterials.map(m => (
                  <option key={m} value={m}>
                    {m}
                  </option>
                ))}
              </Form.Select>
            </Col>
            <Col xs={6} md={4}>
              <Form.Label className="fs-10 mb-1">Diameter</Form.Label>
              <Form.Select
                size="sm"
                disabled={specFrozen || !drumMaterial}
                value={drumDiameter}
                onChange={e => {
                  setPreview(null);
                  setDrumDiameter(e.target.value);
                }}
              >
                <option value="">— choose —</option>
                {diametersFor(drumMaterial).map(d => (
                  <option key={d} value={d}>
                    Ø{d}
                  </option>
                ))}
              </Form.Select>
            </Col>
            <Col xs={12} md={4} className="d-flex align-items-end">
              {/* How many metres fit is a function of the cable, not of the
                  drum alone. Without the standard's outer Ø there is no honest
                  figure, so none is shown rather than a guess. */}
              <div className="fs-10">
                {maxMeters != null ? (
                  <>
                    <div className="text-700">Max on this drum</div>
                    <div className={overCapacity ? 'text-danger fw-bold' : 'fw-bold'}>
                      {fmt(maxMeters)} m
                    </div>
                    {/* The same link the plan dialog carries: the winding, the
                        layers and the drum in 3D, prefilled. New tab, so this
                        modal and its unsaved edit stay where they are. */}
                    <a
                      href={drumLengthStandardUrl({
                        product,
                        drumMaterial,
                        drumDiameter,
                        length: lengthPerUnit
                      })}
                      target="_blank"
                      rel="noopener noreferrer"
                      title="Open the drum-length page for this cable and drum, in 3D"
                    >
                      <FontAwesomeIcon icon={faUpRightFromSquare} className="me-1" />
                      see it on the drum
                    </a>
                  </>
                ) : (
                  <span className="text-700">
                    {cableOuterDiameter > 0
                      ? 'Choose a type and diameter'
                      : 'Cable diameter unknown — no capacity shown'}
                  </span>
                )}
              </div>
            </Col>
          </Row>
        )}

        {overCapacity && (
          <Alert variant="warning" className="py-2 fs-9 mt-3 mb-0">
            <FontAwesomeIcon icon={faTriangleExclamation} className="me-1" />
            {fmt(lengthPerUnit)} m will not fit on a {drumMaterial} Ø{drumDiameter} — it holds
            about {fmt(maxMeters)} m of this cable.
          </Alert>
        )}

        <div className="fs-9 mt-3">
          <span className="text-700">New line total: </span>
          <b>{fmt(totalAfter)} m</b>
          {Math.abs(totalAfter - totalBefore) > 0.5 && (
            <span className={totalAfter > totalBefore ? 'text-success ms-2' : 'text-warning ms-2'}>
              {totalAfter > totalBefore ? '+' : '−'}
              {fmt(Math.abs(totalAfter - totalBefore))} m
            </span>
          )}
          {Math.abs(totalAfter - totalBefore) <= 0.5 && totalAfter > 0 && (
            <span className="text-700 ms-2">— unchanged, only the split differs</span>
          )}
        </div>

        {blockers.length > 0 && (
          <Alert variant="danger" className="py-2 fs-9 mt-3 mb-0">
            <FontAwesomeIcon icon={faTriangleExclamation} className="me-1" />
            {blockers.map((b, i) => (
              <div key={i}>{b}</div>
            ))}
          </Alert>
        )}
        {err && <Alert variant="danger" className="py-2 fs-9 mt-3 mb-0">{err}</Alert>}

        {preview && (
          <div className="mt-3">
            {preview.warnings?.length > 0 && (
              <Alert variant="warning" className="py-2 fs-9 mb-2">
                {preview.warnings.map((w, i) => (
                  <div key={i}>{w}</div>
                ))}
              </Alert>
            )}

            {/* The part worth previewing. A job that promised 20 units of
                150 m owes 3 000 m; after a re-spec it must hold 30 units of
                100 m or it has quietly been cut. */}
            {preview.jobChanges?.length > 0 ? (
              <Alert variant="secondary" className="py-2 fs-9 mb-0">
                <div className="fw-semibold mb-1">Promises that move:</div>
                {preview.jobChanges.map(c => (
                  <div key={c.jobId}>
                    {c.productLabel || c.jobCode || c.jobId}: {c.from} → {c.to} unit(s){' '}
                    <Badge bg={c.reason.includes('trimmed') ? 'warning' : 'info'} className="fs-10 ms-1">
                      {c.reason}
                    </Badge>
                  </div>
                ))}
              </Alert>
            ) : (
              <Alert variant="secondary" className="py-2 fs-9 mb-0">
                No product has promised anything against this item — nothing else moves.
              </Alert>
            )}
          </div>
        )}

        <Form.Group className="mt-3">
          <Form.Label className="fs-10 mb-1">
            Why — this goes into the order&apos;s and every affected product&apos;s history
          </Form.Label>
          <Form.Control
            size="sm"
            value={reason}
            onChange={e => setReason(e.target.value)}
            placeholder="e.g. quantity and length were entered the wrong way round"
          />
        </Form.Group>
      </Modal.Body>

      <Modal.Footer className="d-flex justify-content-between">
        <div className="fs-10 text-700">
          {changedFields.length ? `changing ${changedFields.join(', ')}` : 'nothing changed yet'}
        </div>
        <div className="d-flex gap-2">
          <Button variant="phoenix-secondary" size="sm" onClick={onHide}>
            Close
          </Button>
          <Button
            variant="outline-primary"
            size="sm"
            disabled={!changedFields.length || busy}
            onClick={() => call(true)}
          >
            {busy ? <Spinner animation="border" size="sm" /> : 'Preview'}
          </Button>
          <Button
            variant="primary"
            size="sm"
            // Nothing is written until the consequence has been seen: this can
            // move promises on products nobody editing the order is looking at.
            disabled={!changedFields.length || busy || !preview}
            onClick={() => call(false)}
          >
            Save
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
};

export default EditPlanItemModal;
